import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of, Subject, timer } from 'rxjs';
import { catchError, filter, map, pairwise, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { getCurrentProject } from '@completion/selectors';
import { SupplierService } from '../../core/services/supplier.service';
import { User, UserCheckSheetAssignmentProjection } from '@completion/models';
import { FormControl } from '@angular/forms';
import { CompletionStatus } from '@completion/enums';
import { ProjectState } from '../../core/store/reducers/project';
import {
  buildProcurementPackageGroups,
  detectPackageFocusChange,
  findGroupAssignmentByTag,
  getAssignmentProgressPercent,
  getGroupProgressPercent,
  normalizeCompletionStatus,
  ProcurementPackageGroup,
} from '../supplier-scope.utils';

type AssignmentWithUsers = UserCheckSheetAssignmentProjection & {
  users: User[];
  state?: string | null;
};

type ScopeProcurementPackageGroup = ProcurementPackageGroup<AssignmentWithUsers>;

@Component({
  selector: 'app-scope-view',
  standalone: false,
  templateUrl: './scope-view.component.html',
  styleUrls: ['./scope-view.component.scss'],
})
export class ScopeViewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  loading$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string | null>(null);

  // Expansion state per procurement package
  private expandedMap = new Map<string, boolean>();
  private refresh$ = new BehaviorSubject<number>(0);

  groups$: Observable<ScopeProcurementPackageGroup[]>;

  displayedColumns: string[] = [
    'commPkg',
    'mcPkg',
    'system',
    'tag',
    'checkSheet',
    'company',
    'mcSite',
    'phase',
    'status',
    'progress',
    'users',
  ];

  // Search and highlight
  scopeSearch = new FormControl('');
  highlightedAssignmentId: number | null = null;
  private latestGroups: ScopeProcurementPackageGroup[] = [];

  // Auto-refresh (20s) and spin animation
  private autoRefresh$ = new BehaviorSubject<boolean>(false);
  autoRefreshing$ = this.autoRefresh$.asObservable();
  spinAnimating = false;

  // Trigger fetching
  private fetchTick$ = new BehaviorSubject<number>(0);

  // Baseline and delta counters for auto-refresh session
  private baselineStatusById = new Map<number, CompletionStatus>();
  private baselineProgressById = new Map<number, number>();
  private baselineCount = 0;
  newOkIds: Set<number> = new Set<number>();
  newPaIds: Set<number> = new Set<number>();
  newPbIds: Set<number> = new Set<number>();

  // Expose enum for template (e.g., completionStatus.OK)
  completionStatus = CompletionStatus;

  // Estimated progress percentage from new OKs relative to baseline
  get okProgressEstimatePct(): number {
    if (!this.baselineCount) return 0;
    const pct = (this.newOkIds.size / this.baselineCount) * 100;
    return Math.max(0, Math.min(100, Math.round(pct * 10) / 10));
  }

  constructor(
    private store: Store<ProjectState>,
    private supplierService: SupplierService,
  ) {}

  // Convert a raw status value into a CompletionStatus enum value
  toCompletionStatus(value: string | null | undefined): CompletionStatus {
    return normalizeCompletionStatus(value);
  }

  ngOnInit(): void {
    const projectId$ = this.store.select(getCurrentProject).pipe(
      map((p) => p?.id as number | null),
      filter((id): id is number => id != null),
    );

    // Build a map of assignmentId -> { assignment, users[] }
    const assignmentMap$ = combineLatest([projectId$, this.fetchTick$]).pipe(
      tap(() => {
        this.loading$.next(true);
        this.error$.next(null);
      }),
      switchMap(([pid]) => {
        return this.supplierService.getUsersWithOpenAssignments(pid).pipe(
          switchMap((users) => {
            if (!users || users.length === 0) return of(new Map<number, AssignmentWithUsers>());
            const calls = users.map(
              (u) =>
                this.supplierService
                  // Typing in service is User[] but backend returns projections; cast accordingly
                  .getOpenAssignmentsForUser(pid, u.id) as unknown as Observable<UserCheckSheetAssignmentProjection[]>,
            );
            return forkJoin(calls).pipe(
              map((lists) => {
                const mapById = new Map<number, AssignmentWithUsers>();
                lists.forEach((list, idx) => {
                  const user = users[idx];
                  (list || []).forEach((a) => {
                    if (typeof a?.id !== 'number') return;
                    const existing = mapById.get(a.id);
                    if (existing) {
                      // Append user if not already present
                      if (!existing.users.find((u) => u.id === user.id)) existing.users.push(user);
                    } else {
                      mapById.set(a.id, { ...(a as UserCheckSheetAssignmentProjection), users: [user] });
                    }
                  });
                });
                return mapById;
              }),
            );
          }),
          catchError((err) => {
            this.error$.next('Failed to load scope');
            console.error('[ScopeView] load error', err);
            return of(new Map<number, AssignmentWithUsers>());
          }),
          tap(() => this.loading$.next(false)),
        );
      }),
    );

    // Group by procurement package
    this.groups$ = combineLatest([assignmentMap$, this.refresh$]).pipe(
      map(([mapById]) => buildProcurementPackageGroups(Array.from(mapById.values()), this.expandedMap)),
    );

    // Track latest groups for search/highlight
    this.groups$.pipe(takeUntil(this.destroy$)).subscribe((gs) => (this.latestGroups = gs));

    // Detect progress movement while auto-refresh is ON and focus the most changed
    this.groups$.pipe(pairwise(), withLatestFrom(this.autoRefresh$), takeUntil(this.destroy$)).subscribe(([[prev, next], on]) => {
      if (!on) return;
      // Update counters relative to baseline and then focus changes
      this.updateAutoRefreshStats(next);
      this.detectAndFocusChanges(prev, next);
    });

    // Auto-refresh: immediate and then every 20s while enabled
    this.autoRefreshing$
      .pipe(
        switchMap((on) => (on ? timer(0, 20000) : EMPTY)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.spinAnimating = true;
        setTimeout(() => (this.spinAnimating = false), 800);
        this.fetchTick$.next(Date.now());
      });
  }

  toggleGroup(packageKey: string): void {
    this.expandedMap.set(packageKey, !this.expandedMap.get(packageKey));
    this.refresh$.next(Date.now());
  }

  // Package-level progress based on assignment-level progress values (0..1)
  getGroupProgress(g: ScopeProcurementPackageGroup): number {
    return getGroupProgressPercent(g);
  }

  // Row-level progress: convert 0..1 to percentage and clamp
  getProgress(a: AssignmentWithUsers): number {
    return getAssignmentProgressPercent(a);
  }

  // Search for tag number, expand matching package, highlight and scroll
  findTag(): void {
    const q = (this.scopeSearch.value || '').toString().trim().toLowerCase();
    this.highlightedAssignmentId = null;
    if (!q) {
      this.refresh$.next(Date.now());
      return;
    }
    const match = findGroupAssignmentByTag(this.latestGroups, q);
    if (match) {
      this.expandedMap.set(match.group.packageKey, true);
      this.highlightedAssignmentId = match.assignment.id ?? null;
      this.refresh$.next(Date.now());
      setTimeout(
        () => document.getElementById(`scope-assignment-${match.assignment.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        50,
      );
    }
  }

  clearHighlight(): void {
    this.highlightedAssignmentId = null;
    this.refresh$.next(Date.now());
  }

  toggleAutoRefresh(): void {
    this.autoRefresh$.next(!this.autoRefresh$.value);
    const enabled = this.autoRefresh$.value;
    if (enabled) {
      // Snapshot baseline from the latest known groups as we turn ON
      this.snapshotBaseline();
      // Reset counters
      this.newOkIds.clear();
      this.newPaIds.clear();
      this.newPbIds.clear();
    }
  }

  private snapshotBaseline(): void {
    this.baselineStatusById.clear();
    this.baselineProgressById.clear();
    let count = 0;
    for (const g of this.latestGroups || []) {
      for (const a of g.assignments || []) {
        if (typeof a?.id !== 'number') continue;
        const id = a.id;
        const status = this.getAssignmentStatus(a);
        const progress = typeof a.progress === 'number' ? a.progress : 0;
        this.baselineStatusById.set(id, status);
        this.baselineProgressById.set(id, progress);
        count++;
      }
    }
    this.baselineCount = count;
  }

  private updateAutoRefreshStats(nextGroups: ScopeProcurementPackageGroup[]): void {
    if (!Array.isArray(nextGroups)) return;

    // If baseline is empty (e.g., auto-refresh toggled before data arrived), snapshot from this first emission
    if (this.baselineStatusById.size === 0 && (nextGroups?.length ?? 0) > 0) {
      this.baselineStatusById.clear();
      this.baselineProgressById.clear();
      let count = 0;
      for (const g of nextGroups) {
        for (const a of g.assignments || []) {
          if (typeof a?.id !== 'number') continue;
          const id = a.id;
          const status = this.getAssignmentStatus(a);
          const progress = typeof a.progress === 'number' ? a.progress : 0;
          this.baselineStatusById.set(id, status);
          this.baselineProgressById.set(id, progress);
          count++;
        }
      }
      this.baselineCount = count;
    }

    if (this.baselineStatusById.size === 0) return;
    for (const g of nextGroups) {
      for (const a of g.assignments || []) {
        const id = a?.id as number;
        if (typeof id !== 'number') continue;
        const curStatus = this.getAssignmentStatus(a);
        const baseStatus = this.baselineStatusById.get(id);

        // Completed since baseline: only count if we had the assignment at baseline and it changed to OK
        if (baseStatus !== undefined && baseStatus !== CompletionStatus.OK && curStatus === CompletionStatus.OK) {
          this.newOkIds.add(id);
        }

        // Arrived PA/PB since start: count if new or transitioned to PA/PB
        if ((baseStatus === undefined || baseStatus !== CompletionStatus.PA) && curStatus === CompletionStatus.PA) {
          this.newPaIds.add(id);
        }
        if ((baseStatus === undefined || baseStatus !== CompletionStatus.PB) && curStatus === CompletionStatus.PB) {
          this.newPbIds.add(id);
        }
      }
    }
  }

  private detectAndFocusChanges(prevGroups: ScopeProcurementPackageGroup[], nextGroups: ScopeProcurementPackageGroup[]): void {
    const focus = detectPackageFocusChange(prevGroups, nextGroups, (assignment) => this.getAssignmentStatus(assignment));
    if (focus) {
      this.expandedMap.set(focus.packageKey, true);
      this.highlightedAssignmentId = focus.id;
      this.refresh$.next(Date.now());
      setTimeout(
        () => document.getElementById(`scope-assignment-${focus!.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        50,
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getAssignmentStatus(assignment: AssignmentWithUsers): CompletionStatus {
    return this.toCompletionStatus(assignment.status ?? assignment.state);
  }
}
